import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Percent, Euro, Calendar, Users, Check, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminCoupons = () => {
  const { adminToken } = useAdminAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_discount_amount: '',
    usage_limit: '',
    valid_from: '',
    valid_until: '',
    active: true,
    description: ''
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/coupons/admin/coupons`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      console.error('Errore caricamento coupon:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCoupon
        ? `${process.env.REACT_APP_BACKEND_URL}/api/coupons/admin/coupons/${editingCoupon.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/coupons/admin/coupons`;
      
      const method = editingCoupon ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : 0,
          max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
          usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
        })
      });
      
      if (response.ok) {
        await loadCoupons();
        setShowModal(false);
        resetForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Errore durante il salvataggio');
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      min_order_amount: coupon.min_order_amount?.toString() || '',
      max_discount_amount: coupon.max_discount_amount?.toString() || '',
      usage_limit: coupon.usage_limit?.toString() || '',
      valid_from: coupon.valid_from || '',
      valid_until: coupon.valid_until || '',
      active: coupon.active,
      description: coupon.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo coupon?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/coupons/admin/coupons/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (response.ok) {
        await loadCoupons();
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
    }
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_discount_amount: '',
      usage_limit: '',
      valid_from: '',
      valid_until: '',
      active: true,
      description: ''
    });
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}%`;
    }
    return `€${coupon.discount_value}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">Caricamento...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Coupon</h1>
            <p className="text-gray-600 mt-1">Crea e gestisci i codici sconto</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuovo Coupon
          </button>
        </div>

        {/* Coupons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coupons.map((coupon) => (
            <div
              key={coupon.id}
              className={`bg-white rounded-xl shadow-md p-6 border-2 ${
                coupon.active ? 'border-green-200' : 'border-gray-200'
              }`}
            >
              {/* Header Card */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl font-bold text-purple-600">
                      {coupon.code}
                    </span>
                    {coupon.active ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {coupon.discount_type === 'percentage' ? (
                      <Percent className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Euro className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="text-xl font-bold text-gray-900">
                      {formatDiscount(coupon)}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(coupon)}
                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(coupon.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {coupon.description && (
                <p className="text-sm text-gray-600 mb-4">{coupon.description}</p>
              )}

              {/* Info */}
              <div className="space-y-2 text-sm">
                {coupon.min_order_amount > 0 && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Euro className="w-4 h-4" />
                    <span>Minimo: €{coupon.min_order_amount}</span>
                  </div>
                )}
                
                {coupon.usage_limit && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{coupon.usage_count}/{coupon.usage_limit} utilizzi</span>
                  </div>
                )}
                
                {coupon.valid_until && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Scade: {new Date(coupon.valid_until).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Form */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {editingCoupon ? 'Modifica Coupon' : 'Nuovo Coupon'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Codice */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Codice Coupon *
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="ES: SCONTO20"
                      required
                    />
                  </div>

                  {/* Tipo Sconto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo Sconto *
                    </label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="percentage">Percentuale (%)</option>
                      <option value="fixed">Fisso (€)</option>
                    </select>
                  </div>

                  {/* Valore Sconto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valore Sconto * {formData.discount_type === 'percentage' ? '(%)' : '(€)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder={formData.discount_type === 'percentage' ? '15' : '10.00'}
                      required
                    />
                  </div>

                  {/* Importo Minimo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Importo Minimo Ordine (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.min_order_amount}
                      onChange={(e) => setFormData({...formData, min_order_amount: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Sconto Massimo (solo per percentuale) */}
                  {formData.discount_type === 'percentage' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sconto Massimo (€) - opzionale
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.max_discount_amount}
                        onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="100.00"
                      />
                    </div>
                  )}

                  {/* Limite Utilizzi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Limite Utilizzi (opzionale)
                    </label>
                    <input
                      type="number"
                      value={formData.usage_limit}
                      onChange={(e) => setFormData({...formData, usage_limit: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Illimitato"
                    />
                  </div>

                  {/* Date Validità */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valido Dal
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valido Fino
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Descrizione */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrizione
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows="3"
                      placeholder="Descrizione del coupon..."
                    />
                  </div>

                  {/* Attivo */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Coupon Attivo
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      {editingCoupon ? 'Aggiorna' : 'Crea'} Coupon
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons;
